import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import API_BASE_URL, { getImageUrl } from '../config';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';
import NotificationCenter from './NotificationCenter';
import RoleSelectionModal from './RoleSelectionModal';

const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout, openProfileModal } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [shortlistedCount, setShortlistedCount] = useState(0);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkShortlist = () => {
      const saved = localStorage.getItem('shadigram_shortlist');
      if (saved) setShortlistedCount(JSON.parse(saved).length);
    };
    checkShortlist();
    window.addEventListener('storage', checkShortlist);
    return () => window.removeEventListener('storage', checkShortlist);
  }, []);

  // Detect if we're on a page with a cinematic dark hero
  const isHeroDark = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navClasses = [
    'navbar',
    'glass',
    isHeroDark && !scrolled ? 'navbar-hero-dark' : '',
    scrolled ? 'navbar-scrolled' : '',
  ].filter(Boolean).join(' ');

  const handleLogout = () => {
    logout();
    history.push('/');
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (isMobile && location.pathname === '/') {
    return null;
  }

  return (
    <nav className={navClasses}>
      <div className="navbar-brand">
        <Link to={
          !isAuthenticated ? "/" : 
          user?.role === 'admin' ? "/admin/dashboard" : 
          user?.role === 'superadmin' ? "/superadmin/dashboard" : 
          user?.role === 'photographer' ? "/photographer/dashboard" : 
          user?.role === 'agency' ? "/agency/" : "/"
        }>
          <img
            src="/shadigram-logo-bold.png"
            alt="Shadi Gram — Shadi Wali App"
            className="navbar-logo"
          />
        </Link>
      </div>

      {/* Navigation links removed from global Navbar as per user request to reduce redundancy */}

      <div className="nav-actions">
        {isAuthenticated ? (
          <div className="nav-profile-section">
            <div className="notif-wrapper mobile-only-inline">
              <NotificationCenter />
            </div>

            <div className={`nav-dropdown ${dropdownOpen ? 'open' : ''}`}
              onClick={() => setDropdownOpen(!dropdownOpen)}>
              <div className="profile-pill">
                <div className="nav-avatar">
                  {user?.profilePhoto ? (
                    <img src={getImageUrl(user.profilePhoto)} alt={user.name} className="dropdown-avatar" />
                  ) : (
                    <span>{getInitials(user?.name || 'U')}</span>
                  )}
                </div>
                <span 
                  className="nav-user-name desktop-only clickable-nav-name"
                  title="Click to view profile"
                  onClick={(e) => {
                    e.stopPropagation();
                    openProfileModal();
                    setDropdownOpen(false);
                  }}
                >
                  {user?.name?.split(' ')[0]}
                </span>
                <span className="dropdown-arrow">▾</span>
              </div>

              <div className="dropdown-menu">
                <div 
                  className="dropdown-header clickable-dropdown-header"
                  title="Click to view profile"
                  onClick={(e) => {
                    e.stopPropagation();
                    openProfileModal();
                    setDropdownOpen(false);
                  }}
                >
                  <div className="header-top">
                    <span className="user-name">{user?.name}</span>
                    <span className="role-tag">{user?.role}</span>
                  </div>
                  {user?.role === 'admin' && user?.weddingPlan && (
                    <div className={`plan-tag ${user.weddingPlan}`}>
                      <span className="plan-dot"></span>
                      {user.weddingPlan.toUpperCase()} PLAN
                    </div>
                  )}
                  <span className="dropdown-profile-hint">View Profile & Details →</span>
                </div>

                <div className="dropdown-divider"></div>

                <div className="dropdown-items-group">
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openProfileModal();
                      setDropdownOpen(false);
                    }} 
                    className="dropdown-item profile-item"
                  >
                    <span className="item-icon">👤</span>
                    <span className="item-label">My Profile</span>
                  </button>

                  <div className="dropdown-item desktop-only">
                    <NotificationCenter showLabel={true} />
                  </div>

                  {(user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'photographer' || user?.role === 'agency') && (
                    <Link to={
                      user.role === 'admin' ? "/admin/dashboard" :
                        user.role === 'photographer' ? "/photographer/dashboard" :
                          user.role === 'agency' ? "/agency/" :
                            "/superadmin/dashboard"
                    } className="dropdown-item highlight">
                      <span className="item-icon">
                        {user.role === 'admin' ? '📊' : user.role === 'photographer' ? '📸' : user.role === 'agency' ? '🏢' : '👑'}
                      </span>
                      <span className="item-label">
                        {user.role === 'admin' ? 'Admin Dashboard' : user.role === 'photographer' ? 'Photographer Portal' : user.role === 'agency' ? 'Agency Portal' : 'Super Admin Panel'}
                      </span>
                    </Link>
                  )}

                  {user?.role === 'guest' && (
                    <>
                      <Link to="/guest/food" className="dropdown-item">
                        <span className="item-icon">🍲</span>
                        <span className="item-label">Order Food</span>
                      </Link>
                      <Link to="/guest/mehendi-ar" className="dropdown-item">
                        <span className="item-icon">🎨</span>
                        <span className="item-label">Mehendi Try-On</span>
                      </Link>
                      <Link to="/guest/vendors" className="dropdown-item">
                        <span className="item-icon">🏪</span>
                        <span className="item-label">Vendors</span>
                      </Link>
                      <Link to="/guest/schedule" className="dropdown-item">
                        <span className="item-icon">📅</span>
                        <span className="item-label">Schedule</span>
                      </Link>
                      <Link to="/guest/tasks" className="dropdown-item">
                        <span className="item-icon">✅</span>
                        <span className="item-label">My Tasks</span>
                      </Link>
                    </>
                  )}
                </div>

                <div className="dropdown-divider"></div>

                <button onClick={handleLogout} className="dropdown-item logout-item">
                  <span className="item-icon">🚪</span>
                  <span className="item-label">Logout</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="nav-profile-section">
            <Link to="/login" className="profile-pill unauth-pill">
              <div className="nav-avatar soft-gold-bg">
                <span>👤</span>
              </div>
            </Link>
          </div>
        )}
      </div>

      <RoleSelectionModal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        shortlistedCount={shortlistedCount}
      />
    </nav>
  );
};

export default Navbar;
